import { DatePipe } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { merge, Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';

import { Option } from '@ptg-shared/controls/select/select.component';
import { deepClone } from '@ptg-shared/utils/common.util';
import { EmployerFilter } from '@ptg-employer/models/employer-filter.model';

import * as StatusActions from '../../store/actions/status.actions';
import {
  AgePropertyName,
  FilterPropertyType,
  HeaderType,
  INPUT_TYPE,
  Operator,
  OPERATOR_BY_TYPE,
  OPERATOR_LABEL,
  PROPERTY_TYPE,
  SystemProperties,
} from '../../constance/member-list.const';
import { InputType } from '../../constance/metadataPropertyType.const';
import {
  FilterByPropertyConfigEntity,
  FilterInputType,
  MemberFilter,
  MemberFilterEntity,
  MemberStatus,
  PropertyItem,
} from '../../types/models';
import * as fromMember from '../../store/reducers';
import { MemberListActions } from '../../store/actions';
import { OptionValue } from '@ptg-shared/types/models';
import { SCREEN_ID } from '@ptg-member/constance/lookupTable.const';
import { EntityPropertyType } from '@ptg-entity-management/types/enums/entity-property.enum';
import { VERTICAL_LINE_SEPARATOR } from '@ptg-shared/constance/common.const';

@Component({
  selector: 'ptg-add-member-filters',
  templateUrl: './add-member-filters.component.html',
  styleUrls: ['./add-member-filters.component.scss']
})
export class AddMemberFiltersComponent implements OnInit, OnDestroy {
  unsubscribe$ = new Subject<void>();
  listSectionProperty: Option[] = [];
  filterByOptions: Option[] = [];
  filterOperators: Option[] = [];
  filterFormGroup: FormGroup = this.fb.group({
    filterBy: this.fb.control('', [Validators.required]),
    operator: this.fb.control('', [Validators.required]),
    valueFrom: this.fb.control('', [Validators.required]),
    valueTo: this.fb.control('', [this.validateBetweenValue()]),
  });
  currentFilters: MemberFilterEntity[] = [];
  placeholder = {
    from: 'Value',
    to: 'Value To',
  };
  TEXT_BOX_INPUT_TYPE = InputType;
  inputType: FilterInputType = INPUT_TYPE[FilterPropertyType.Text];
  FIXED_PROPERTIES: Record<string, string> = {
    MemberiId: 'Member Id',
    Registered: 'Registered',
    Locked: 'Locked',
    last4Ssn: 'SSN',
    PaperlessOptedIn: 'Paperless Opted-In',
    SMSOptedIn: 'SMS Opted-In',
    FixedMember: 'FixedMember',
    FixedParticipant: 'FixedParticipant',
    FixedPerson: 'FixedPerson'
  };

  booleanOptions: Option[] = [
    { displayValue: 'Yes', value: true },
    { displayValue: 'No', value: false },
  ];
  inputConfig: {
    suffix: string;
    isPositive: boolean;
    isDecimal: boolean;
    allowZero: boolean;
  } = {
    suffix: '',
    isPositive: true,
    isDecimal: true,
    allowZero: true,
  };
  statusOptions: Option[] = [];
  eventOptions: Option[] = [];
  eventOperatorOptions: Option[] = [];
  dateOperatorOptions: Option[] = [];
  valueOptions: Option[] = [];
  isMultipleLineOption: boolean = false;
  propertyConfigs: any = [];
  listCalculationAndAggregation: number[] = [EntityPropertyType.Calculation, EntityPropertyType.Aggregation];
  constructor(
    private fb: FormBuilder,
    private memberStore: Store<fromMember.MemberState>,
    public dialogRef: MatDialogRef<AddMemberFiltersComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { 
      currentFilters: MemberFilterEntity[],
      entityId: string
     }
  ) {}

  ngOnInit(): void {
    this.getPropertyOfParticipant();
    this.memberStore.dispatch(
      MemberListActions.getFilterByPropertyListRequest()
    );
    this.memberStore.dispatch(
      StatusActions.getStatus({ query: { SortNames: 'Name', SortType: 0 } })
    );

    this.memberStore
      .select(fromMember.selectMemberStatusList)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((statusList) => {
        this.statusOptions = (deepClone(statusList) as MemberStatus[]).map(
          (status) => {
            return {
              value: status.id,
              displayValue: status.name,
            };
          }
        );
      });

    this.memberStore
      .select(fromMember.selectStatusDetail)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((statusDetailState) => {
        if (
          this.filterFormGroup.value.filterBy.type !==
            FilterPropertyType.Status ||
          !this.filterFormGroup.value.valueFrom
        ) {
          this.eventOptions = [];
          return;
        }
        this.eventOptions = (
          statusDetailState?.statusDetail?.memberEvents || []
        ).map((event) => {
          return {
            value: event.id,
            displayValue: event.name,
          };
        });
      });
    this.updateValidityFormGroup();

    this.memberStore
    .pipe(
      select(fromMember.selectPropertyOfParticipants),
      takeUntil(this.unsubscribe$)
    )
    .subscribe((res: any) => {
      if(res?.propertyList?.propertyOfParticipants.length) {
        const propertyOfParticipants = deepClone(res?.propertyList?.propertyOfParticipants);
        this.mapSectionPropertyData(propertyOfParticipants);
        if (this.data?.currentFilters) {
          this.currentFilters = deepClone(this.data.currentFilters);
          this.currentFilters.forEach(value => {
            if(value.entityReferencePropertyId) {
              value.id = `${value?.id?.split(VERTICAL_LINE_SEPARATOR)[0]}|${value.entityReferencePropertyId}`;
            }
          });
          this.getFilterByOptions();
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  getPropertyOfParticipant() {
    const query = {
      isIncludePropertyEntityReference: true,
      isIncludePropertyOfList: true,
      isUseFilter: true,
      screenId: SCREEN_ID.Participant
    };
    this.memberStore.dispatch(MemberListActions.getPropertyOfParticipantsRequestRequest({ entityId: this.data.entityId, query }));
  }

  mapSectionPropertyData(listSectionProperty: FilterByPropertyConfigEntity[]) {
    this.listSectionProperty = listSectionProperty?.reduce((result, item) => {
      if(item.propertyNameEntityRef) {
        item.id = `${item.id}|${item.propertyRefId}`;
      }
      const propertyConfig = JSON.parse(
        JSON.stringify(item)
      ) as FilterByPropertyConfigEntity;  
      if (
        [
          FilterPropertyType.Address,
          FilterPropertyType['Person Name'],
        ].includes(propertyConfig.type)
      ) {
        propertyConfig.options.forEach((option) => {
          const currentOption = JSON.parse(JSON.stringify(option));
          const config = JSON.parse(
            JSON.stringify(item)
          ) as FilterByPropertyConfigEntity;
          if (currentOption.value === 'Prefix') {
            currentOption.value = 'Salutation';
          }
          if (currentOption) {
            config.isRequired = currentOption.isRequired;
          }
          config.options = [currentOption];
          config.propertyTypeLabel = `${
            FilterPropertyType[config.type]
          } - ${currentOption.value}`;
          result.push({
            value: config,
            displayValue: `${config.propertyName} - ${currentOption.value}`,
            valueDescription: `${this.getType(propertyConfig.propertyNameEntityRef ? 54 : 
              this.listCalculationAndAggregation.includes(propertyConfig.displayDataType) ? propertyConfig.displayDataType : propertyConfig.type)} 
              ${propertyConfig?.componentName && propertyConfig?.componentName !== PROPERTY_TYPE || propertyConfig?.componentNameRef && propertyConfig?.componentNameRef !== PROPERTY_TYPE ? ' / ' + 
              (propertyConfig?.componentNameRef && propertyConfig.componentNameRef !== PROPERTY_TYPE ? propertyConfig.componentNameRef : propertyConfig.componentName) : ''}
              ${propertyConfig.propertyNameEntityRef ? ' / ' + propertyConfig.propertyNameEntityRef : ''}`,
          });
        });
        return result;
      }
      propertyConfig.propertyTypeLabel =
        this.FIXED_PROPERTIES[propertyConfig.propertyKey] ||
        FilterPropertyType[propertyConfig.type];
      result.push({
        value: propertyConfig,
        displayValue: `${propertyConfig.propertyName}`,
        valueDescription: `${propertyConfig.type === FilterPropertyType.Status ? this.getType(propertyConfig.propertyNameEntityRef ? 54 : 203) : 
          this.getType(propertyConfig.propertyNameEntityRef ? 54 : this.listCalculationAndAggregation.includes(propertyConfig.displayDataType) ? propertyConfig.displayDataType : propertyConfig.type)} 
        ${(propertyConfig?.componentName && propertyConfig?.componentName !== PROPERTY_TYPE || propertyConfig?.componentNameRef && propertyConfig?.componentNameRef !== PROPERTY_TYPE) ? ' / ' + 
        (propertyConfig?.componentNameRef && propertyConfig.componentNameRef !== PROPERTY_TYPE ? propertyConfig.componentNameRef : propertyConfig.componentName) : ''}
        ${propertyConfig.propertyNameEntityRef ? ' / ' + propertyConfig.propertyNameEntityRef : ''}`,
      });
      return result;
    }, [] as Option[]);

    this.filterByOptions = [...this.listSectionProperty];
  }

  getType(entityPropertyType: EntityPropertyType): string {
    if (
      entityPropertyType === EntityPropertyType.Calculation ||
      entityPropertyType === EntityPropertyType.System ||
      entityPropertyType === EntityPropertyType.Aggregation ||
      entityPropertyType === EntityPropertyType['Entity Reference'] ||
      entityPropertyType === EntityPropertyType.Identifier
    ) {
      return EntityPropertyType[entityPropertyType];
    } else {
      return 'Data';
    }
  }

  onFilterBySelect() {
    this.valueOptions = [];
    if (
      this.filterFormGroup.value.filterBy.type ===
      FilterPropertyType.Status
    ) {
      this.handleFilterStatus();
      this.valueOptions = deepClone(this.statusOptions);
      return;
    }
    const filterBy = this.filterFormGroup.value.filterBy;
    this.filterFormGroup = this.initFilterForm(this.filterFormGroup.value);
    this.inputType = INPUT_TYPE[filterBy.type];
    if (filterBy.type === FilterPropertyType.System) {
      if (SystemProperties.includes(filterBy.propertyKey)) {
        this.valueOptions = deepClone(this.booleanOptions);
      } else if (filterBy.propertyKey === AgePropertyName) {
        filterBy.propertyTypeLabel = AgePropertyName;
        this.inputType = INPUT_TYPE[4];
      }
    }
    if (
      [
        'Address - State/Province/Territory',
        'Address - Country',
        'Person Name - Salutation',
      ].includes(filterBy.propertyTypeLabel) ||
      filterBy.isUsedLookupTable ||
      filterBy.type === FilterPropertyType.Boolean ||
      filterBy.type === FilterPropertyType.Department ||
      filterBy.type === FilterPropertyType.Employer
    ) {
      this.inputType = 'Boolean';
    }

    const currentProperty = this.listSectionProperty.find((property) => {
      return (
        property.value.propertyKey === filterBy.propertyKey &&
        property.value.propertyTypeLabel === filterBy.propertyTypeLabel
      );
    }) as any;
    const isRequired = currentProperty?.value?.isRequired;
    const isEncrypted = currentProperty?.value?.isEncryptedProperty;
    const isMultipleLineOption =
      filterBy.type === FilterPropertyType.Department;
    if (filterBy.type === FilterPropertyType.Department) {
      this.isMultipleLineOption = isMultipleLineOption;
    }
    if (filterBy.type === FilterPropertyType.Employer) {
      this.isMultipleLineOption = false;
    }
    if (
      currentProperty?.value?.options?.[0]?.optionValues?.length ||
      currentProperty?.value?.optionValues?.length
    ) {
      let optionValues = [];
      if(currentProperty?.value?.options?.[0]?.optionValues.length) {
        optionValues = currentProperty?.value?.options?.[0]?.optionValues;
      } else if (currentProperty?.value?.optionValues.length) {
        optionValues =  currentProperty?.value?.optionValues;
      }
      this.valueOptions = deepClone(
        optionValues
      ).map((option: OptionValue) => {
        return {
          displayValue: option.text,
          value: this.getOptionValue(option),
          valueDescription: this.isMultipleLineOption
            ? option.description
            : undefined,
        };
      });
    } else if (
      currentProperty?.value?.type === FilterPropertyType.List &&
      currentProperty?.value?.isUsedLookupTable
    ) {
      this.valueOptions = deepClone(
        currentProperty?.value?.optionValues.length ? currentProperty?.value?.optionValues : []
      ).map((option: OptionValue) => {
        return {
          displayValue: option.text,
          value: option.id,
        };
      });
    }
    const aggregation = filterBy?.configs?.aggregation;
    this.filterOperators = this.getOperatorOptions(
      filterBy.type === FilterPropertyType['Whole Number'] 
      && aggregation
      && !JSON.parse(JSON.stringify(aggregation))?.aggregationtype ? 'Count' : filterBy.propertyTypeLabel,
      isRequired,
      isEncrypted
    );  
    this.inputConfig = this.getInputConfig(
        filterBy.propertyTypeLabel
    );
    this.filterFormGroup.get('operator')?.setValue('');
    this.filterFormGroup.get('valueFrom')?.setValue('');
    this.filterFormGroup.get('valueTo')?.setValue('');
    this.onFilterOperatorSelect();
  }

  getOptionValue(option: OptionValue) {
    switch (option.type) {
      case 4: {
        return true;
      }
      case 5: {
        return false;
      }
      default: {
        return option.id;
      }
    }
  }

  onFilterOperatorSelect() {
    const operator = this.filterFormGroup.value.operator;
    const operatorOptionsStatus = this.getOperatorOptions('StatusEvent');
    if (this.filterFormGroup.get('filterBy')?.value?.type === FilterPropertyType.Status) {
      if (operator === Operator.NotEquals || operator === Operator.IsAny) {
        this.eventOperatorOptions = operatorOptionsStatus.filter(x => x.value === Operator.IsAny);
      } else {
        this.eventOperatorOptions = operatorOptionsStatus;
      }
      if (operator === Operator.IsAny) {
        this.filterFormGroup = this.fb.group({
          ...this.filterFormGroup.controls,
          valueFrom: [{
            value: '',
            disabled: true
          }],
          valueTo: [{
            value: '',
            disabled: true
          }],
          event: this.fb.group({
            operator: [{
              value: '',
              disabled: false
            }],
            valueFrom: [{
              value: '',
              disabled: true
            }],
          }),
          date: this.fb.group({
            operator: [{
              value: '',
              disabled: true
            }],
            valueFrom: [{
              value: '',
              disabled: true
            }],
            valueTo: [{
              value: '',
              disabled: true
            }],
          }),
        });
      } else {
        this.filterFormGroup = this.fb.group({
          ...this.filterFormGroup.controls,
          valueFrom: this.fb.control('', [Validators.required]),
          valueTo: this.fb.control('', [this.validateBetweenValue()]),
          event: this.fb.group({
            operator: [{
              value: '',
              disabled: true
            }],
            valueFrom: [{
              value: '',
              disabled: true
            }],
          }),
          date: this.fb.group({
            operator: [{
              value: '',
              disabled: true
            }],
            valueFrom: [{
              value: '',
              disabled: true
            }],
            valueTo: [{
              value: '',
              disabled: true
            }],
          }),
        });
      }
      return;
    }

    switch (operator) {
      case Operator.Between: {
        this.placeholder.from = 'Value From';
        this.filterFormGroup.get('valueTo')?.addValidators(Validators.required);
        this.filterFormGroup.get('valueFrom')?.enable();
        break;
      }
      case Operator.IsEmpty:
      case Operator.IsNotEmpty: {
        this.filterFormGroup.get('valueFrom')?.reset('');
        this.filterFormGroup.get('valueFrom')?.disable();
        this.placeholder.from = 'Value';
        this.filterFormGroup
          .get('valueTo')
          ?.removeValidators(Validators.required);
        break;
      }
      default: {
        this.filterFormGroup.get('valueFrom')?.enable();
        this.placeholder.from = 'Value';
        this.filterFormGroup
          .get('valueTo')
          ?.removeValidators(Validators.required);
      }
    }
    this.filterFormGroup.get('valueTo')?.updateValueAndValidity({ emitEvent: false });
  }

  getOperatorOptions(
    filterType: PropertyItem,
    isRequired: boolean = false,
    isEncrypted: boolean = false
  ) {
    return (deepClone(OPERATOR_BY_TYPE[filterType] || []) as Operator[])
      .filter((operator) => {
        return (
          !(
            isRequired &&
            [Operator.IsEmpty, Operator.IsNotEmpty].includes(operator)
          ) &&
          !(
            isEncrypted &&
            filterType !== 'SSN' &&
            ![
              Operator.IsEmpty,
              Operator.IsNotEmpty,
              Operator.Equals,
              Operator.NotEquals,
            ].includes(operator)
          )
        );
      })
      .map((operator) => {
        let displayValue = OPERATOR_LABEL[Operator[operator]];
        if (
          ['DateTime', 'Date'].includes(filterType) &&
          ['GreaterThan', 'LessThan'].includes(Operator[operator])
        ) {
          displayValue = OPERATOR_LABEL['Date' + Operator[operator]];
        }
        return {
          value: operator,
          displayValue,
        };
      });
  }

  addFilter() {
    this.filterFormGroup.markAllAsTouched();
    if (this.filterFormGroup.invalid) {
      return;
    }
    const formValue = this.filterFormGroup.value;
    let propertyType = formValue.filterBy.type;
    const values = [formValue.valueFrom, formValue.valueTo].reduce(
      (result, currentValue) => {
        if (currentValue === '') {
          return result;
        }
        switch (propertyType) {
          case FilterPropertyType.Date: {
            if (currentValue) {
              result.push(
                (currentValue as DateTime)
                  .toUTC(0, { keepLocalTime: true })
                  .toISO()
              );
            }
            break;
          }
          case FilterPropertyType.Status: {
            if (currentValue) {
              result.push(
                this.statusOptions.find(
                  (status) => status.value === currentValue
                )?.displayValue
              );
            }
            break;
          }
          default: {
            result.push(currentValue);
          }
        }
        return result;
      },
      []
    );
    let filterValue: MemberFilterEntity = {
      id: formValue.filterBy.id,
      propertyKey: formValue.filterBy.propertyKey,
      operator: formValue.operator,
      type: formValue.filterBy.type,
      propertyName: this.getPropertyName(formValue),
      values,
      options: formValue.filterBy?.options?.[0]?.key,
      selectedPropertyItem: `${formValue.filterBy.propertyName}${
        formValue.filterBy.options?.length &&
        formValue.filterBy.type !== FilterPropertyType.Boolean
          ? ` - ${formValue.filterBy?.options?.[0]?.value}`
          : ''
      }`,
      optionValues: this.getOptionValues(),
      entityId: formValue.filterBy.entityId,
      propertyRefId: formValue.filterBy.propertyRefId,
      statusNameId: formValue.filterBy.type === FilterPropertyType.Status ? [formValue.valueFrom] : [],
    };
    if (formValue.filterBy.type === FilterPropertyType.Status) {
      filterValue.options = 'Name';
      const event = {
        options: 'Event',
        operator: formValue.event.operator,
        values: [
          this.eventOptions.find(
            (event) => event.value === formValue.event.valueFrom
          )?.displayValue || '',
        ],
        statusEventId: [formValue.event.valueFrom]
      };
      let date;
      if (formValue.date?.operator) {
        date = {
          options: 'Date',
          operator: formValue.date.operator,
          values: [formValue.date.valueFrom],
        };

        if (formValue.date.operator === Operator.IsCurrentStatus) {
          const fromValue = (formValue.date.valueFrom as DateTime)
            .toUTC(0, { keepLocalTime: true })
            .toISO();
          date.values = [fromValue];
        }

        if (formValue.date.operator === Operator.Between) {
          const fromValue = (formValue.date.valueFrom as DateTime)
            .toUTC(0, { keepLocalTime: true })
            .toISO();
          const toValue = (formValue.date.valueTo as DateTime)
            .toUTC(0, { keepLocalTime: true })
            .toISO();
          date.values = [fromValue, toValue];
        }
      }
      filterValue = {
        ...filterValue,
        event,
        date,
      };
      if (date) {
        filterValue = {
          ...filterValue,
          date,
        };
      }
    }
    this.eventOptions = [];
    this.currentFilters = [...this.currentFilters, filterValue];
    this.filterFormGroup = this.initFilterForm();
    this.filterOperators = [];
    this.inputType = 'Text';
    this.placeholder.from = 'Value';
    this.getFilterByOptions();
  }

  private getPropertyName(formValue: any): string{
    return formValue?.filterBy?.propertyName;
  }

  getInputConfig(propertyItem: PropertyItem) {
    switch (propertyItem) {
      case 'Decimal':
        return {
          allowZero: true,
          isDecimal: true,
          isPositive: false,
          suffix: '',
        };
      case 'Currency':
        return {
          allowZero: true,
          isDecimal: true,
          isPositive: false,
          suffix: '$',
        };
      case 'Percentage':
        return {
          allowZero: true,
          isDecimal: true,
          isPositive: true,
          suffix: '%',
        };
      case 'Phone':
      case 'Address - Zip Code/Postal Code':
      case 'Address - Country':
        return {
          allowZero: true,
          isDecimal: false,
          isPositive: true,
          suffix: '',
        };
      case 'Whole Number':
        return {
          allowZero: true,
          isDecimal: false,
          isPositive: false,
          suffix: '',
        };
      default:
        return {
          allowZero: true,
          isDecimal: true,
          isPositive: true,
          suffix: '',
        };
    }
  }

  removeFilterCondition(removedFilter: MemberFilterEntity | EmployerFilter) {
    const removedIndex = this.currentFilters.findIndex(
      (filter) =>
        filter.id === removedFilter.id &&
        filter.options === removedFilter.options
    );
    if (removedIndex > -1) {
      this.currentFilters.splice(removedIndex, 1);
      this.getFilterByOptions();
      this.filterFormGroup.reset('');
    }
  }

  getFilterByOptions() {
    this.filterByOptions = deepClone(this.listSectionProperty).filter(
      (item: any) => {
        return !this.currentFilters.find(
          (filter) =>
            filter.id === item.value.id &&
            (filter.type === FilterPropertyType.Status ||
              (filter.options &&
                filter.options === item.value.options?.[0]?.key) ||
              !filter.options)
        );
      }
    );
  }

  onCancel() {
    this.dialogRef.close();
  }

  applyFilter() {
    this.dialogRef.close(this.currentFilters);
  }

  validateBetweenValue(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (
        !control.parent ||
        control.parent.get('operator')?.value !== Operator.Between
      ) {
        return null;
      }
      const { value: valueFrom } = control.parent.get(
        'valueFrom'
      ) as FormControl;
      const { value: valueTo } = control.parent.get('valueTo') as FormControl;
      const isStatusDate =
        control.parent?.parent?.value?.filterBy?.type ===
        FilterPropertyType.Status;
      const isDate =
        control.parent?.get('filterBy')?.value.type ===
        FilterPropertyType.Date;
      const isDateTime =
        control.parent?.get('filterBy')?.value.type ===
        FilterPropertyType.DateTime;
      if (valueFrom && valueTo && (isDate || isDateTime || isStatusDate)) {
        return DateTime.fromISO(valueFrom) >= DateTime.fromISO(valueTo)
          ? {
              fromToError: `The input value must be greater than ${new DatePipe(
                'en-US'
              ).transform(
                valueFrom,
                isDateTime ? 'MM/dd/yyyy hh:mm a' : 'MM/dd/yyyy'
              )}.`,
            }
          : null;
      }
      if (
        valueTo?.length &&
        valueFrom?.length &&
        (+valueFrom > +valueTo || +valueFrom === +valueTo)
      ) {
        return {
          fromToError: `The input value must be greater than ${valueFrom}.`,
        };
      }
      return null;
    };
  }

  updateValidityFormGroup() {
    merge(
      this.filterFormGroup.controls['valueFrom'].valueChanges,
      this.filterFormGroup.controls['valueTo'].valueChanges
    ).subscribe(() => {
      this.filterFormGroup
        .get('valueTo')
        ?.updateValueAndValidity({ emitEvent: false });
    });
  }

  handleFilterStatus() {
    this.filterFormGroup = this.fb.group({
      ...this.filterFormGroup.controls,
      operator: this.fb.control('', [Validators.required]),
      valueFrom: [{
        value: '',
        disabled: true
      }],
      valueTo: [{
        value: '',
        disabled: true
      }],
      event: this.fb.group({
        operator: [{
          value: '',
          disabled: true
        }],
        valueFrom: [{
          value: '',
          disabled: true
        }],
      }),
      date: this.fb.group({
        operator:[{
          value: '',
          disabled: true
        }],
        valueFrom: [{
          value: '',
          disabled: true
        }],
        valueTo: this.fb.control('', [this.validateBetweenValue()]),
      }),
    });
    this.filterOperators = this.getOperatorOptions('StatusName');
    this.inputType = 'Boolean';
  }

  changeStatus() {
    if (
      this.filterFormGroup.value.filterBy.type !==
      FilterPropertyType.Status
    ) {
      return;
    }
    const statusId = this.filterFormGroup.get('valueFrom')?.value;
    this.filterFormGroup = this.fb.group({
      ...this.filterFormGroup.controls,
      event: this.fb.group({
        operator: this.fb.control(''),
        valueFrom:  [{
          value: '',
          disabled: true
        }],
      }),
      date: this.fb.group({
        operator: [{
          value: '',
          disabled: true
        }],
        valueFrom:  [{
          value: '',
          disabled: true
        }],
        valueTo: this.fb.control('', [this.validateBetweenValue()]),
      }),
    });
    this.memberStore.dispatch(
      StatusActions.getStatusDetail({
        query: { SortNames: 'Name', SortType: 0, Id: statusId },
      })
    );
  }

  changeDateOperator() {
    const dateOperator = this.filterFormGroup.value?.date?.operator;
    switch (dateOperator) {
      case Operator.AtAnyPoint: {
        this.filterFormGroup.get('date')?.get('valueFrom')?.disable();
        this.filterFormGroup.get('date')?.get('valueTo')?.disable();
        break;
      }
      case Operator.Between: {
        if (
          this.filterFormGroup.value.date.valueFrom &&
          !DateTime.isDateTime(this.filterFormGroup.value.date.valueFrom)
        ) {
          this.filterFormGroup.get('date')?.get('valueFrom')?.setValue('');
        }
        this.filterFormGroup.get('date')?.get('valueTo')?.enable();
        this.filterFormGroup.get('date')?.get('valueFrom')?.enable();
        this.filterFormGroup
          .get('date')
          ?.get('valueTo')
          ?.addValidators(Validators.required);
        break;
      }
      case Operator.IsCurrentStatus: {
        this.filterFormGroup.get('date')?.get('valueTo')?.disable();
        this.filterFormGroup.get('date')?.get('valueFrom')?.enable();
        if (
          this.filterFormGroup.value.date.valueFrom &&
          !DateTime.isDateTime(this.filterFormGroup.value.date.valueFrom)
        ) {
          this.filterFormGroup.get('date')?.get('valueFrom')?.setValue('');
        }
        break;
      }
      default: {
        this.filterFormGroup.get('date')?.get('valueTo')?.disable();
        this.filterFormGroup.get('date')?.get('valueFrom')?.enable();
        this.filterFormGroup
          .get('date')
          ?.get('valueFrom')
          ?.addValidators(Validators.required);
      }
    }
    (this.filterFormGroup?.controls['date'] as FormGroup)?.controls[
      'valueFrom'
    ]?.updateValueAndValidity();
  }

  initFilterForm(formValue?: any) {
    return this.fb.group({
      filterBy: this.fb.control(formValue?.filterBy, [Validators.required]),
      operator: this.fb.control('', [Validators.required]),
      valueFrom: this.fb.control('', [Validators.required]),
      valueTo: this.fb.control('', [this.validateBetweenValue()]),
    });
  }

  onEventOperatorSelect() {
    const operatorEventDate = this.getOperatorOptions('StatusDate');
    if (this.filterFormGroup.value?.operator === Operator.IsAny && this.filterFormGroup.value?.event?.operator === Operator.IsAny) {
      this.dateOperatorOptions = operatorEventDate.filter(x => x.value !== Operator.AtAnyPoint);
    } else {
      this.dateOperatorOptions = operatorEventDate; 
    }
    if(this.filterFormGroup.value?.event?.operator === Operator.IsAny ){
      this.filterFormGroup = this.fb.group({
        ...this.filterFormGroup.controls,
        event: this.fb.group({
          operator: this.filterFormGroup.value?.event.operator,
          valueFrom: [{
            value: '',
            disabled: true
          }],
        }),
        date: this.fb.group({
          operator: [{
            value: '',
            disabled: false
          }],
          valueFrom:  [{
            value: '',
            disabled: true
          }],
          valueTo: this.fb.control('', [this.validateBetweenValue()]),
        })
      });
    } else {
      this.filterFormGroup = this.fb.group({
        ...this.filterFormGroup.controls,
        event: this.fb.group({
          operator: this.filterFormGroup.value?.event.operator,
          valueFrom: this.fb.control('',[Validators.required]),
        }),
        date: this.fb.group({
          operator: [{
            value: '',
            disabled: true
          }],
          valueFrom:  [{
            value: '',
            disabled: true
          }],
          valueTo: this.fb.control('', [this.validateBetweenValue()]),
        }),
      });
    
    }
  }

  onEventValueSelect() {
    this.filterFormGroup = this.fb.group({
      ...this.filterFormGroup.controls,
      date: this.fb.group({
        operator: [{
          value: '',
          disabled: false
        }],
        valueFrom:  [{
          value: '',
          disabled: true
        }],
        valueTo: this.fb.control('', [this.validateBetweenValue()]),
      }),
    });
  }

  changeDateValue() {
    (this.filterFormGroup.controls['date'] as FormGroup)?.controls[
      'valueTo'
    ].updateValueAndValidity();
  }

  onValueFromChange(event: any) {
    this.filterFormGroup
      .get('valueTo')
      ?.updateValueAndValidity({ emitEvent: false });
  }

  onChangeFromDate() {
    this.filterFormGroup
      .get('valueTo')
      ?.updateValueAndValidity({ emitEvent: false });
  }

  getOptionValues() {
    const formValue = this.filterFormGroup.value;
    let optionValues = [];
    if(formValue?.filterBy?.optionValues?.length) {
      optionValues = formValue?.filterBy?.optionValues
    } else if(formValue?.filterBy?.options?.[0]?.optionValues?.length) {
      optionValues = formValue?.filterBy?.options?.[0]?.optionValues
    }
    return optionValues?.map((optionValue: OptionValue) => {
      return {
        id: optionValue.id,
        text: optionValue.text,
        type: optionValue.type,
      };
    });
  }
}
